/**
 * TestDefinitionSchedulingSection.tsx
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md) — B2: the Scheduling
 * section of the one-object detail pane. Edits the PRIMARY task type's
 * operational fields (Run Mode / minutes / preset days / machine qty / skill)
 * plus the catalog item's Default Machine, and opens the shared
 * TaskTypeMachinesEditor ("Machines & Time" — one source of truth, D5).
 *
 * When the item has no primary task type (shouldn't happen post-098, coded
 * defensively) it shows an amber callout with a one-click repair button.
 */

import { useMemo } from 'react';
import {
  Alert, Autocomplete, Badge, Box, Button, Checkbox, FormControlLabel,
  MenuItem, Stack, TextField, Typography, useTheme,
} from '@mui/material';
import { Build, HardwareOutlined, DriveFileMoveOutlined } from '@mui/icons-material';
import type { Skill } from '../../../types/schedule';
import type { TestDefinitionItem } from '../../../services/testDefinitionService';
import { continuousSpanDays, type TaskRunMode } from '../../../utils/runMode';
import { capableOptionsForTest, guidEquals, type MachineOption } from './testCatalogHelpers';
import { SectionHeader, type DraftFieldSetter } from './TestDefinitionFormSections';
import type { DefinitionDraft } from './testDefinitionModel';

interface SchedulingSectionProps {
  draft: DefinitionDraft;
  setField: DraftFieldSetter;
  /** Null in create mode. */
  item: TestDefinitionItem | null;
  /** False when the create-mode "also create matching task type" switch is OFF. */
  showTaskTypeFields: boolean;
  skills: Skill[];
  machineOptions: readonly MachineOption[];
  capableMap: Record<string, string[]>;
  /** Opens the shared TaskTypeMachinesEditor for the primary task type. */
  onOpenMachinesEditor: () => void;
  /** Repairs a missing primary task type (create + link recipe row). */
  onCreateMatchingTaskType: () => void;
  repairing: boolean;
}

export const TestDefinitionSchedulingSection = ({
  draft,
  setField,
  item,
  showTaskTypeFields,
  skills,
  machineOptions,
  capableMap,
  onOpenMachinesEditor,
  onCreateMatchingTaskType,
  repairing,
}: SchedulingSectionProps) => {
  const theme = useTheme();
  const primary = item?.primaryTaskType ?? null;
  const isCreate = item === null;
  const isContinuous = draft.ttRunMode === 'continuous';
  const capabilityCount = primary?.CapabilityCount ?? 0;

  // Default Machine: capable-only picker; falls back to all machines with a
  // caption when nothing is mapped yet (the pre-existing REQ-06 behavior).
  const capableOptions = useMemo(
    () => capableOptionsForTest(item?.TestCatalogItemKey, capableMap, machineOptions),
    [item, capableMap, machineOptions],
  );
  const noCapableMapped = capableOptions.length === 0;
  const machinePickerOptions = noCapableMapped ? machineOptions : capableOptions;
  const selectedMachineValue = useMemo(() => {
    if (!draft.DefaultMachineKey) return '';
    const match = machinePickerOptions.find((m) => guidEquals(m.value, draft.DefaultMachineKey));
    return match?.value ?? '';
  }, [draft.DefaultMachineKey, machinePickerOptions]);

  const skillOptions = useMemo(
    () => [...skills].sort((a, b) =>
      (a.Category ?? 'zz').localeCompare(b.Category ?? 'zz')
      || a.Name.localeCompare(b.Name)),
    [skills],
  );
  const selectedSkill = skillOptions.find((s) => guidEquals(s.SkillKey, draft.ttSkillKey)) ?? null;

  const defaultMinutesNum = draft.ttDefaultMinutes.trim() !== ''
    ? parseInt(draft.ttDefaultMinutes, 10)
    : null;

  return (
    <Box>
      <SectionHeader
        title="Scheduling"
        action={primary && (
          <Badge badgeContent={capabilityCount} color={capabilityCount === 0 ? 'warning' : 'primary'} showZero>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HardwareOutlined />}
              onClick={onOpenMachinesEditor}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Machines &amp; Time
            </Button>
          </Badge>
        )}
      />

      {/* Migration 118: tri-state scheduling mode (supersedes the 111 Include
          checkbox — the BE keeps IncludeInSchedule converged). */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          select
          size="small"
          label="Scheduling"
          value={draft.SchedulingMode}
          onChange={(e) => setField('SchedulingMode', e.target.value as DefinitionDraft['SchedulingMode'])}
          fullWidth
        >
          <MenuItem value="machine">Machine scheduling (default)</MenuItem>
          <MenuItem value="people">People effort only — no machine</MenuItem>
          <MenuItem value="none">Not scheduled</MenuItem>
        </TextField>
        {draft.SchedulingMode === 'machine' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Quote and work-order lines for this test are added to scenario planning
            as machine tasks.
          </Typography>
        )}
        {draft.SchedulingMode === 'people' && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            People-effort line (e.g. Lab Project Management) — lines on this item
            create NO machine tasks or bookings. They pool into one staffing line
            per job in the People phase, where the crew and weekly % are set by hand.
          </Alert>
        )}
        {draft.SchedulingMode === 'none' && (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            Excluded from scheduling — quote and work-order lines for this test are
            SKIPPED by scenario planning (no schedule tasks, prep, or bookings).
            Quoting and pricing are unaffected.
          </Alert>
        )}
      </Box>

      {/* Defensive: no primary task type — quotable but silently unschedulable. */}
      {!isCreate && !primary && (
        <Alert
          severity="warning"
          action={(
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<Build />}
              onClick={onCreateMatchingTaskType}
              disabled={repairing}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {repairing ? 'Creating…' : 'Create matching task type'}
            </Button>
          )}
        >
          This test has no scheduling task type — work-order lines on it generate
          NO schedule tasks. Create the matching task type to make it schedulable.
        </Alert>
      )}

      {showTaskTypeFields && (isCreate || primary) && (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5}>
            <TextField
              select
              size="small"
              label="Run Mode"
              value={draft.ttRunMode}
              onChange={(e) => setField('ttRunMode', e.target.value as TaskRunMode)}
              fullWidth
            >
              <MenuItem value="attended">Attended (default)</MenuItem>
              <MenuItem value="continuous">Continuous (24/7)</MenuItem>
            </TextField>
            {isContinuous && (
              <TextField
                size="small"
                label="Attended Minutes"
                value={draft.ttAttendedMinutes}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v !== '' && !/^\d+$/.test(v)) return;
                  setField('ttAttendedMinutes', v);
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 }}
                placeholder="e.g. 120"
                helperText="Person minutes per test"
                fullWidth
              />
            )}
          </Stack>
          {isContinuous && (
            <Typography
              variant="caption"
              sx={{ color: theme.schedule.maintenance, fontWeight: 600 }}
            >
              Continuous: runs 24/7 once started — machine time counts calendar days
              {defaultMinutesNum != null && defaultMinutesNum > 0
                ? ` (~${continuousSpanDays(defaultMinutesNum)} calendar days at ${defaultMinutesNum} min)`
                : ''}
              . Existing plans keep their spans until you run Sync now or Replan.
            </Typography>
          )}
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="number"
              label="Preset Days"
              value={draft.ttPresetDurationDays}
              onChange={(e) => setField('ttPresetDurationDays', e.target.value)}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              label="Machine Qty"
              value={draft.ttDefaultMachineCount}
              onChange={(e) => setField('ttDefaultMachineCount', e.target.value)}
              inputProps={{ min: 1 }}
              fullWidth
            />
            <TextField
              size="small"
              label="Default Minutes"
              value={draft.ttDefaultMinutes}
              onChange={(e) => {
                const v = e.target.value;
                if (v !== '' && !/^\d+$/.test(v)) return;
                setField('ttDefaultMinutes', v);
              }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
              placeholder="e.g. 6300"
              helperText="Fallback machine time"
              fullWidth
            />
          </Stack>
          <Autocomplete
            size="small"
            options={skillOptions}
            value={selectedSkill}
            groupBy={(s) => s.Category ?? 'No category'}
            getOptionLabel={(s) => s.Name}
            isOptionEqualToValue={(o, v) => guidEquals(o.SkillKey, v.SkillKey)}
            onChange={(_e, value) => setField('ttSkillKey', value?.SkillKey ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Required Skill"
                placeholder="None (no skill required)"
                helperText="Who may run this test — the scheduling mentor gate reads this."
              />
            )}
          />
          {/* Index Tests Phase 1 / I1, I5: admin-only flag + capacity, edit-mode
              only — the flag/capacity door is the existing transactional PUT. */}
          {!isCreate && (
            <Box>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={draft.ttIsIndexTest}
                    onChange={(e) => setField('ttIsIndexTest', e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    Index test (pooled on the shared Index machine, tests/day)
                  </Typography>
                )}
              />
              {draft.ttIsIndexTest && (
                <TextField
                  size="small"
                  label="Tests per day"
                  value={draft.ttIndexTestsPerDay}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
                    setField('ttIndexTestsPerDay', v);
                  }}
                  inputProps={{ inputMode: 'decimal', maxLength: 8 }}
                  placeholder="e.g. 8"
                  helperText="Capacity on the shared virtual Index machine — decimals allowed"
                  sx={{ mt: 1, maxWidth: 260 }}
                />
              )}
            </Box>
          )}
        </Stack>
      )}

      {/* Migration 119 (P2, read-only): the lab's measured per-test person
          minutes — feeds the Phase-3 hybrid FTE demand. Edit UI deferred
          ("admin-editable, to a point"); values change via the DB seed. */}
      {primary?.effortProfile && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.66rem' }}>
            Measured person time (per test)
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
            {[
              ['Prep', primary.effortProfile.UnpackCutMinutes + primary.effortProfile.GrindingMinutes + primary.effortProfile.ConformityMinutes],
              ['Test', primary.effortProfile.TestMinutes],
              ['Report', primary.effortProfile.ReportMinutes],
              ['Review', primary.effortProfile.ReviewMinutes],
              ['Approve', primary.effortProfile.ApproveMinutes],
            ].map(([label, mins]) => (
              <Typography key={label} variant="caption" sx={{ fontSize: '0.72rem' }}>
                {label}: <b>{mins} min</b>
              </Typography>
            ))}
            <Typography variant="caption" sx={{ fontSize: '0.72rem', color: theme.palette.primary.main }}>
              Total: <b>{primary.effortProfile.TotalMinutes} min ({(primary.effortProfile.TotalMinutes / 60).toFixed(2)} h)</b>
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.66rem', mt: 0.25 }}>
            From the lab&rsquo;s measured table — drives the scheduling &ldquo;Rough demand&rdquo;
            estimate for this test (its price is excluded from the cost-based half).
          </Typography>
        </Box>
      )}

      {/* Default Machine (catalog-side) — capable-only picker (REQ-06). */}
      <Box sx={{ mt: showTaskTypeFields ? 2 : 1 }}>
        <TextField
          select
          size="small"
          label="Default Machine"
          value={selectedMachineValue}
          onChange={(e) => setField('DefaultMachineKey', e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <DriveFileMoveOutlined fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />
            ),
          }}
        >
          <MenuItem value=""><em>No default</em></MenuItem>
          {machinePickerOptions.map((m) => (
            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
          ))}
        </TextField>
        {noCapableMapped ? (
          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
            {isCreate
              ? 'Capable machines will be listed once the test is saved and mapped under Machines & Time. Showing all active machines for now.'
              : 'No capable machines mapped for this test yet — add them via Machines & Time. Showing all active machines.'}
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Only machines capable of this test are listed. Auto-scheduling prefers this machine.
          </Typography>
        )}
      </Box>
    </Box>
  );
};
